import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import employerConsultantApi from '../../services/employerConsultantApi';
import './ConsultantJobApproval.css';

const ConsultantJobApproval = () => {
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchPendingJobs();
  }, []);

  const fetchPendingJobs = async () => {
    try {
      setLoading(true);
      const response = await employerConsultantApi.getPendingConsultantJobs();
      setPendingJobs(response.data.jobs);
    } catch (error) {
      toast.error('Failed to fetch pending jobs');
      console.error('Fetch pending jobs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (jobId) => {
    if (!window.confirm('Are you sure you want to approve and publish this job?')) {
      return;
    }

    try {
      await employerConsultantApi.approveConsultantJob(jobId);
      toast.success('Job approved and published successfully');
      fetchPendingJobs();
      setShowDetailsModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to approve job');
      console.error('Approve job error:', error);
    }
  };

  const handleReject = async (jobId) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;

    try {
      await employerConsultantApi.rejectConsultantJob(jobId, reason);
      toast.success('Job rejected');
      fetchPendingJobs();
      setShowDetailsModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to reject job');
      console.error('Reject job error:', error);
    }
  };

  const viewJobDetails = (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const renderJobCard = (job) => (
    <div key={job._id} className="cja-card">
      <div className="cja-card-top">
        <div className="cja-card-icon">
          <span className="material-symbols-outlined">work</span>
        </div>
        <div className="cja-card-title">
          <h3>{job.title}</h3>
          <p>
            <span className="material-symbols-outlined">person</span>
            {job.consultantId?.name}
          </p>
        </div>
        <span className="cja-pending-badge">
          <span className="material-symbols-outlined">schedule</span>
          Pending Review
        </span>
      </div>

      <div className="cja-card-meta">
        {job.jobType?.name && (
          <span><span className="material-symbols-outlined">work_history</span>{job.jobType.name}</span>
        )}
        {(job.location?.city || job.workMode) && (
          <span><span className="material-symbols-outlined">location_on</span>{job.location?.city || job.workMode}</span>
        )}
        {job.salary?.min && (
          <span><span className="material-symbols-outlined">currency_rupee</span>{job.salary.min}–{job.salary.max} {job.salary.currency}</span>
        )}
        <span><span className="material-symbols-outlined">calendar_today</span>Submitted {new Date(job.createdAt).toLocaleDateString()}</span>
      </div>

      {job.description && (
        <p className="cja-card-desc">{job.description.substring(0, 140)}{job.description.length > 140 ? '…' : ''}</p>
      )}

      <div className="cja-card-actions">
        <button className="cja-btn-view" onClick={() => viewJobDetails(job)}>
          <span className="material-symbols-outlined">visibility</span>View Details
        </button>
        <button className="cja-btn-success" onClick={() => handleApprove(job._id)}>
          <span className="material-symbols-outlined">check_circle</span>Approve
        </button>
        <button className="cja-btn-danger" onClick={() => handleReject(job._id)}>
          <span className="material-symbols-outlined">cancel</span>Reject
        </button>
      </div>
    </div>
  );

  return (
    <div className="emp-consultant-approval cja-container">
      <div className="cja-header">
        <div>
          <h1 className="cja-title">Job Approvals</h1>
          <p className="cja-subtitle">Review and approve jobs submitted by consultants</p>
        </div>
        {pendingJobs.length > 0 && (
          <span className="cja-count-badge">
            <span className="material-symbols-outlined">pending_actions</span>
            {pendingJobs.length} Pending
          </span>
        )}
      </div>

      {loading ? (
        <div className="cja-loading"><div className="cja-spinner"></div><p>Loading…</p></div>
      ) : pendingJobs.length === 0 ? (
        <div className="cja-empty">
          <span className="material-symbols-outlined">task_alt</span>
          <h3>All caught up!</h3>
          <p>No pending job approvals at this time</p>
        </div>
      ) : (
        <div className="cja-grid">
          {pendingJobs.map(renderJobCard)}
        </div>
      )}

      {showDetailsModal && selectedJob && (
        <div className="cja-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="cja-modal" onClick={e => e.stopPropagation()}>
            <div className="cja-modal-header">
              <div>
                <h2>{selectedJob.title}</h2>
                <p>by {selectedJob.consultantId?.name} &bull; {selectedJob.consultantId?.email}</p>
              </div>
              <button className="cja-modal-close" onClick={() => setShowDetailsModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="cja-modal-body">
              <div className="cja-modal-section">
                <h4><span className="material-symbols-outlined">info</span>Job Details</h4>
                <div className="cja-info-grid">
                  {selectedJob.jobType?.name && <div><span>Type</span><strong>{selectedJob.jobType.name}</strong></div>}
                  {selectedJob.workMode && <div><span>Mode</span><strong>{selectedJob.workMode}</strong></div>}
                  {selectedJob.shiftType?.name && <div><span>Shift</span><strong>{selectedJob.shiftType.name}</strong></div>}
                  {selectedJob.industryType?.name && <div><span>Industry</span><strong>{selectedJob.industryType.name}</strong></div>}
                  {selectedJob.location?.city && <div><span>Location</span><strong>{selectedJob.location.city}{selectedJob.location.state ? `, ${selectedJob.location.state}` : ''}</strong></div>}
                  {selectedJob.experienceRequired && <div><span>Experience</span><strong>{selectedJob.experienceRequired.min}–{selectedJob.experienceRequired.max} yrs</strong></div>}
                  {selectedJob.salary && <div><span>Salary</span><strong>{selectedJob.salary.min}–{selectedJob.salary.max} {selectedJob.salary.currency}/{selectedJob.salary.period}{selectedJob.salary.negotiable ? ' (Neg.)' : ''}</strong></div>}
                </div>
              </div>

              {selectedJob.description && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">description</span>Description</h4>
                  <p className="cja-modal-text">{selectedJob.description}</p>
                </div>
              )}

              {selectedJob.responsibilities && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">list_alt</span>Responsibilities</h4>
                  <p className="cja-modal-text">{selectedJob.responsibilities}</p>
                </div>
              )}

              {selectedJob.requirements && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">checklist</span>Requirements</h4>
                  <p className="cja-modal-text">{selectedJob.requirements}</p>
                </div>
              )}

              {selectedJob.requiredSkills?.length > 0 && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">psychology</span>Required Skills</h4>
                  <div className="cja-tags">
                    {selectedJob.requiredSkills.map(s => <span key={s._id} className="cja-tag">{s.name}</span>)}
                  </div>
                </div>
              )}

              {selectedJob.optionalSkills?.length > 0 && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">lightbulb</span>Optional Skills</h4>
                  <div className="cja-tags">
                    {selectedJob.optionalSkills.map(s => <span key={s._id} className="cja-tag cja-tag-optional">{s.name}</span>)}
                  </div>
                </div>
              )}

              {selectedJob.benefits?.length > 0 && (
                <div className="cja-modal-section">
                  <h4><span className="material-symbols-outlined">volunteer_activism</span>Benefits</h4>
                  <div className="cja-tags">
                    {selectedJob.benefits.map(b => <span key={b._id} className="cja-tag cja-tag-benefit">{b.name}</span>)}
                  </div>
                </div>
              )}
            </div>

            <div className="cja-modal-footer">
              <button className="cja-btn-success" onClick={() => handleApprove(selectedJob._id)}>
                <span className="material-symbols-outlined">check_circle</span>Approve & Publish
              </button>
              <button className="cja-btn-danger" onClick={() => handleReject(selectedJob._id)}>
                <span className="material-symbols-outlined">cancel</span>Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantJobApproval;
